import { IconNameV1 } from 'common/icon/iconV1.js';
import { LegendTypes } from 'components/legends/legend-types.js';
import { ProjectionCore } from 'components/map/factory/types/projection-types.js';
import { MapTopoLayerType } from 'components/map/factory/types/topo-layer-type.js';
import { IBBQMapKey } from 'components/map/marker-view/collections/bbq/marker.type.bbq.js';
import { StatusLegendMapKey } from 'components/map/marker-view/collections/marine-marker/legend.js';
import { MarkerOptionsMap } from 'components/map/themes/pill-theme.js';
import { PolyOptionsMap } from 'components/map/themes/polyline-theme.js';
import { ICaptureEventProps } from 'components/tracking/types.js';
import { IModuleConfig } from 'redux-state/layout/types.modules.js';
import { CompassDirection } from 'types/compass-direction.js';
import { WindStrength } from 'types/wind.js';
import { ImageBounds } from 'utilities/image/Image-bounds.js';
import { IImageLink } from 'utilities/image/image.js';
import { MarkerDisplayLevels2 } from 'utilities/marker-zoom/zoom-v2.js';
import { ZoomLevelsMap } from 'utilities/marker-zoom/zoom-v3.js';
import { IMarker } from './markerCore/marker-core-type.js';

export interface ICheckedFilter {
  key: string | Array<string>;
  label: string;
  selected: boolean;
  body?: JSX.Element;
  icon?: IconNameV1;
}

export enum LocationScopeType {
  Region = 'region',
  Location = 'location',
}

export interface IObservationType {
  title?: string;
}

export interface IWind extends IObservationType {
  strength?: WindStrength;
  direction: CompassDirection;
  averageSpeed: number;
  gustSpeed?: number;
}

export interface IRain extends IObservationType {
  rainfall: number;
  relativeHumidity: number;
}

export interface ISwell extends IObservationType {
  direction: CompassDirection;
  speed?: string;
  seaHeight?: number;
  swellHeight: number;
  period?: number;
}

export interface IPressure extends IObservationType {
  atSeaLevel: number;
  trend?: 'rising' | 'falling' | 'stable' | 'n/a';
}

export interface IClothing extends IObservationType {
  layers: number;
  windproofLayers: number;
}

export interface IMapKeyContent {
  icon: IconNameV1;
  label: string;
  rating?: number;
  value?: string;
}

export type IMapGeneralKeyContent = {
  icon: IconNameV1;
  label: string;
  content?: IMapKeyContent[];
  // For the marine radars maps the images come from the API, instead of being icons
  // we have in the app. Hence an optional `legendImage` field is required
  // (used at `legend-keys` for LegendTypes.SeaTemp, LegendTypes.WaveHeight, LegendTypes.PeakWavePeriod cases)
  legendImage?: string;
  text?: string;
  type?: Exclude<LegendTypes, CustomMapKeys['type']>;
};

export type CustomMapKeys = IBBQMapKey | StatusLegendMapKey;

export type IMapKey = IMapGeneralKeyContent | CustomMapKeys;

export interface IPolyline {
  colour?: string;
  lines: Array<L.LatLngExpression>;
}

export interface IRegion {
  key: string;
  polygon: Array<L.LatLngTuple>;
  polygons: Array<Array<L.LatLngTuple>>;
}

export interface IMapConfig extends Partial<L.MapOptions> {
  // center: L.LatLngExpression;
  // zoom: number;
  // zoomDelta: number; // levels of zoomIn zoomout
  isZoomable: boolean; // Determines if the map can zoom
  // isDraggable: boolean; // Determines if the map is draggable
  renderAsPins: boolean; // Renders markers as pins UNLESS the marker type is region
  shouldCluster?: boolean; // Turns on clustering of nearby markers at different zoom levels
  showGridLines?: boolean; // Adds grid lines to the map
  // hasDefaultMarker?: boolean; // Allows pre-selection based on location query string
  isStatic?: boolean; // If set to true the app does not use a Leaflet map, but an image with the markers absolute positioned using % (see map-static.jsx)
  isMarineWarningsMap?: boolean; // Will select a region on a warning map instead of dropping a pin
  embedMobileMap?: boolean; // Will display the map in a mobile view
  /** Bounds currently used in mobile view */
  bounds?: {
    imageBounds?: ImageBounds;
    fixToBounds?: ProjectionCore['fixToBounds'];
  };
}

export type MarkerTracking = Pick<ICaptureEventProps, 'category'>;

export type MapTags = {
  markerTags?: MarkerOptionsMap;
  polyTags?: PolyOptionsMap;
  zoomTags?: ZoomLevelsMap;
};

export type IMapV2 = MapTags & {
  config: IMapConfig & { mapLayer?: MapTopoLayerType; gestureHandling?: boolean };
  showMarkerFilters?: boolean;
  keys: Array<IMapKey>;
  checkboxFilters?: Array<ICheckedFilter>;
  checkBoxFilterLabel?: string;
  checkBoxFilterType?: 'warnings';
  radioFilters?: Array<IRadioFilter>;
  radioFilterLabel?: string;
  markers: IMarker[];
  markerDisplay?: MarkerDisplay;
  modules?: Array<IModuleConfig>;
  imageTile?: IImageLink;
  polylines?: Array<IPolyline>;
  regions?: Array<IRegion>;
};

export type IMapRaw = Omit<IMapV2, 'checkboxFilters' | 'checkBoxFilterLabel'> & {
  /** We rename types here as confusing */
  filters: IMapV2['checkboxFilters'];
  filterLabel: IMapV2['checkBoxFilterLabel'];
  filterType?: IMapV2['checkBoxFilterType'];
};

export enum RadioFilterTypes {
  Temperature = 'temperature',
  Rainfall = 'rainfall',
  Wind = 'wind',
  Humidity = 'humidity',
  Pressure = 'pressure',
  SoilTemperature = 'soil-temperature',
  SoilMoisture = 'soil-moisture',
}

export interface IRadioFilter {
  label: string;
  key: RadioFilterTypes;
  dateStart?: string;
  dateEnd?: string;
}

export enum MarkerDisplaySize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export type MarkerDisplayLevels1 = { [key in MarkerDisplaySize]: number };

export type MarkerDisplay = {
  /** @deprecated Use zoom structure levels can be confusing */
  levels?: MarkerDisplayLevels1;
  radioFilter?: RadioFilterTypes;
  /** New standard for V2 */
  zoom?: MarkerDisplayLevels2;
};
